import { useState, useRef } from 'react'
import styles from '../../UpdateSiteModal.module.css'
import { AccessOption, LeadCaptureModeDropdown } from '../AccessSettings/AccessToggle'
import { PLANS } from 'constants/plans'
import { LEAD_CAPTURE_MODE_NONE, LEAD_CAPTURE_MODE_PROMOTIONAL, LEAD_CAPTURE_MODE_EMAIL } from '../../constants'

const FormInput = ({
  label,
  placeholder,
  id,
  value,
  onChange,
  maxLength,
  isTextarea = false,
  disabled = false,
  onClick,
  error
}) => {
  const inputId = id || `input-${label.replace(/\s+/g, '-').toLowerCase()}`
  const remainingChars = maxLength ? maxLength - (value?.length || 0) : null

  const handleClick = (e) => {
    if (disabled && onClick) {
      e.preventDefault()
      onClick()
    }
  }

  const InputComponent = isTextarea ? 'textarea' : 'input'

  return (
    <div className={styles.formGroup}>
      <label htmlFor={inputId}>
        {label}
        {maxLength && (
          <span style={{ color: '#888', fontSize: '12px', marginLeft: '8px' }}>
            ({remainingChars} characters remaining)
          </span>
        )}
      </label>
      <InputComponent
        type={isTextarea ? undefined : 'text'}
        id={inputId}
        className={`${styles.formControl} ${error ? styles.formControlError : ''}`}
        placeholder={placeholder}
        aria-label={label}
        value={value || ''}
        onChange={onChange}
        maxLength={maxLength}
        disabled={disabled}
        onClick={handleClick}
        style={{
          cursor: disabled ? 'pointer' : 'text',
          backgroundColor: disabled ? '#f5f5f5' : 'white',
          ...(isTextarea && { minHeight: '80px', resize: 'vertical' })
        }}
      />
      {error && <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '4px' }}>{error}</div>}
    </div>
  )
}

export const formatUrl = (url) => {
  if (!url || url.trim() === '') return url

  const trimmedUrl = url.trim()

  // Allow mailto links to pass through unchanged
  if (trimmedUrl.startsWith('mailto:')) {
    return trimmedUrl
  }

  // If it already has a protocol, return as is
  if (trimmedUrl.match(/^https?:\/\//)) {
    return trimmedUrl
  }

  // Add https:// prefix for regular URLs
  return `https://${trimmedUrl}`
}

const LeadCaptureSection = ({
  toggleValues,
  setFieldValue,
  userProfile,
  showUpgradeModal,
  errors,
  uploadValues,
  site
}) => {
  const isRestricted = !userProfile?.productId || userProfile?.productId === PLANS.TINY.id
  const [focusedField, setFocusedField] = useState(null)
  const ctaButtonRef = useRef(null)
  const [showLinkTooltip, setShowLinkTooltip] = useState(false)
  const [linkInputFocused, setLinkInputFocused] = useState(false)

  const handleInputChange = (field, value) => {
    setFieldValue(`toggleValues.${field}`, value)
  }

  const handleLeadCaptureModeChange = (mode) => {
    if (isRestricted && mode !== LEAD_CAPTURE_MODE_NONE) {
      showUpgradeModal()
      return
    }

    setFieldValue('toggleValues.leadCaptureMode', mode)
    // For backwards compatibility, also set activatedLeadCapture
    setFieldValue('toggleValues.activatedLeadCapture', mode !== LEAD_CAPTURE_MODE_NONE)
  }

  const checkLeadCaptureFeatureAccess = (mode) => {
    if (mode !== LEAD_CAPTURE_MODE_NONE && isRestricted) {
      showUpgradeModal()
      return false
    }
    return true
  }

  const leadCaptureModeOptions = [
    {
      value: LEAD_CAPTURE_MODE_NONE,
      label: 'None',
      description: 'No lead capture functionality'
    },
    {
      value: LEAD_CAPTURE_MODE_PROMOTIONAL,
      label: 'Promotional slide-in',
      description: 'Activate a slide-in with a custom message to prompt an action'
    },
    {
      value: LEAD_CAPTURE_MODE_EMAIL,
      label: 'Capture Emails',
      description: 'Collect visitor emails before granting access'
    }
  ]

  const currentLeadCaptureMode = toggleValues?.leadCaptureMode || LEAD_CAPTURE_MODE_NONE

  const handleFocus = (field) => {
    setFocusedField(field)
    if (field === 'leadCaptureCtaText') {
      setShowLinkTooltip(true)
    }
  }

  const handleBlur = (field) => {
    // Use setTimeout to allow focus to move to link input if needed
    setTimeout(() => {
      if (field === 'leadCaptureCtaText') {
        // Only hide if focus didn't move to link input
        const activeElement = document.activeElement
        if (!activeElement || !activeElement.classList.contains(styles.linkInput)) {
          setFocusedField(null)
          setShowLinkTooltip(false)
        }
      }
    }, 100)
  }

  const handleLinkInputFocus = () => {
    setFocusedField('leadCaptureCtaLink')
    setShowLinkTooltip(true)
    setLinkInputFocused(true)
  }

  const handleLinkInputBlur = () => {
    setLinkInputFocused(false)
    setTimeout(() => {
      const activeElement = document.activeElement
      // Only hide if focus didn't move back to CTA button
      if (!activeElement || !activeElement.classList.contains(styles.ctaButtonInput)) {
        setShowLinkTooltip(false)
        setFocusedField(null)
      }
    }, 100)
  }

  if (!uploadValues?.selectedFile && !site?.isTransferRequestLink) {
    return null
  }

  return (
    <>
      <div id="lead_capture" className={styles.section}>
        <span className={styles.sectionTitle}>
          <img src="/assets/icons/funnel-filled.svg" alt="widgets" height="24" width="24" />
          <h3 className="mb-0 font-18">Lead Capture </h3>
        </span>

        <div className={styles.accessOptionWrapper}>
          <div className={styles.accessOption}>
            <div className={`${styles.optionIcon} ${styles.leadCaptureIcon}`}>
              <img src="/assets/icons/slide-in.png" alt="lead capture" width="24" />
            </div>
            <div className={styles.optionDetails}>
              <div className={styles.optionTitle}>Lead Capture Mode</div>
              <div className={styles.optionDescription}>Choose how to engage visitors on your site</div>
            </div>
          </div>
          <div className={styles.optionToggle}>
            <LeadCaptureModeDropdown
              options={leadCaptureModeOptions}
              value={currentLeadCaptureMode}
              onChange={handleLeadCaptureModeChange}
              checkFeatureAccess={checkLeadCaptureFeatureAccess}
            />
          </div>
        </div>

        {currentLeadCaptureMode === LEAD_CAPTURE_MODE_PROMOTIONAL && (
          <div className={styles.widgetSettings}>
            <div className={styles.previewContainer}>
              <h4 className={styles.previewTitle}>Slide-in Editor</h4>
              <div className={styles.slideInPreview}>
                <div className={styles.slideInContent}>
                  <div className={styles.inputWithCounter}>
                    <input
                      type="text"
                      className={`${styles.previewInput} ${styles.titleInput}`}
                      placeholder="Enter title here"
                      value={toggleValues?.leadCaptureTitle || ''}
                      onChange={(e) => handleInputChange('leadCaptureTitle', e.target.value)}
                      onFocus={() => handleFocus('leadCaptureTitle')}
                      onBlur={handleBlur}
                      maxLength={50}
                      disabled={isRestricted}
                      data-testid="LeadCapture-input-enter-title"
                    />
                    {focusedField === 'leadCaptureTitle' && (
                      <span className={styles.charCounter}>{toggleValues?.leadCaptureTitle?.length || 0}/50</span>
                    )}
                    {errors?.toggleValues?.leadCaptureTitle && (
                      <div className={styles.errorText}>{errors?.toggleValues?.leadCaptureTitle}</div>
                    )}
                  </div>

                  <div className={styles.inputWithCounter}>
                    <textarea
                      className={styles.previewInput}
                      placeholder="Write a brief description of your offer"
                      value={toggleValues?.leadCaptureDescription || ''}
                      onChange={(e) => handleInputChange('leadCaptureDescription', e.target.value)}
                      onFocus={() => handleFocus('leadCaptureDescription')}
                      onBlur={handleBlur}
                      maxLength={120}
                      disabled={isRestricted}
                      style={{ minHeight: '60px', resize: 'vertical' }}
                      data-testid="LeadCapture-input-enter-description"
                    />
                    {focusedField === 'leadCaptureDescription' && (
                      <span className={styles.charCounter}>
                        {toggleValues?.leadCaptureDescription?.length || 0}/120
                      </span>
                    )}
                    {errors?.toggleValues?.leadCaptureDescription && (
                      <div className={styles.errorText}>{errors?.toggleValues?.leadCaptureDescription}</div>
                    )}
                  </div>

                  <div className={`${styles.ctaContainer} ${showLinkTooltip ? styles.ctaContainerFocused : ''}`}>
                    <div className={styles.ctaButtonWrapper} ref={ctaButtonRef}>
                      <input
                        type="text"
                        className={styles.ctaButtonInput}
                        placeholder="Enter button text"
                        value={toggleValues?.leadCaptureCtaText || ''}
                        onChange={(e) => handleInputChange('leadCaptureCtaText', e.target.value)}
                        onFocus={() => handleFocus('leadCaptureCtaText')}
                        onBlur={() => handleBlur('leadCaptureCtaText')}
                        maxLength={25}
                        disabled={isRestricted}
                      />
                      {focusedField === 'leadCaptureCtaText' && (
                        <span className={styles.charCounter}>{toggleValues?.leadCaptureCtaText?.length || 0}/25</span>
                      )}
                      {errors?.toggleValues?.leadCaptureCtaText && (
                        <div className={styles.errorText}>{errors?.toggleValues?.leadCaptureCtaText}</div>
                      )}
                    </div>

                    {showLinkTooltip && (
                      <div
                        className={`${styles.linkInputContainer} ${linkInputFocused ? styles.linkInputContainerFocused : ''}`}
                        onClick={() => {
                          const linkInput = document.querySelector(`.${styles.linkInput}`)
                          if (linkInput) linkInput.focus()
                        }}
                      >
                        <div className={styles.linkInputWrapper}>
                          <img
                            src="/assets/icons/link.svg"
                            alt="link"
                            width="16"
                            height="16"
                            className={styles.grayIcon}
                          />
                          <input
                            type="text"
                            className={styles.linkInput}
                            placeholder="Type or paste URL"
                            value={toggleValues?.leadCaptureCtaLink || ''}
                            onChange={(e) => handleInputChange('leadCaptureCtaLink', e.target.value)}
                            onFocus={handleLinkInputFocus}
                            onBlur={handleLinkInputBlur}
                            disabled={isRestricted}
                          />
                        </div>
                        {errors?.toggleValues?.leadCaptureCtaLink && (
                          <div className={styles.errorText}>{errors?.toggleValues?.leadCaptureCtaLink}</div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Keep original form inputs for accessibility and fallback */}
            <div className={styles.hiddenInputs}>
              <FormInput
                label="Title"
                className={styles.titleInput}
                id="lead-capture-title-hidden"
                value={toggleValues?.leadCaptureTitle}
                onChange={(e) => handleInputChange('leadCaptureTitle', e.target.value)}
                maxLength={50}
                disabled={isRestricted}
                error={errors?.toggleValues?.leadCaptureTitle}
              />
              <FormInput
                label="Short Description"
                id="lead-capture-description-hidden"
                value={toggleValues?.leadCaptureDescription}
                onChange={(e) => handleInputChange('leadCaptureDescription', e.target.value)}
                maxLength={120}
                isTextarea={true}
                disabled={isRestricted}
                error={errors?.toggleValues?.leadCaptureDescription}
              />
              <FormInput
                label="CTA Link"
                id="lead-capture-cta-link-hidden"
                value={toggleValues?.leadCaptureCtaLink}
                onChange={(e) => handleInputChange('leadCaptureCtaLink', e.target.value)}
                disabled={isRestricted}
                error={errors?.toggleValues?.leadCaptureCtaLink}
              />
              <FormInput
                label="CTA Button Text"
                id="lead-capture-cta-text-hidden"
                value={toggleValues?.leadCaptureCtaText}
                onChange={(e) => handleInputChange('leadCaptureCtaText', e.target.value)}
                maxLength={25}
                disabled={isRestricted}
                error={errors?.toggleValues?.leadCaptureCtaText}
              />
            </div>
          </div>
        )}

        {currentLeadCaptureMode === LEAD_CAPTURE_MODE_EMAIL && (
          <div className={styles.widgetSettings}>
            <div className={styles.previewContainer}>
              <h4 className={styles.previewTitle}>Email Collection Slide-in Editor</h4>
              <div className={styles.slideInPreview}>
                <div className={styles.slideInContent}>
                  <div className={styles.inputWithCounter}>
                    <input
                      type="text"
                      className={`${styles.previewInput} ${styles.titleInput}`}
                      placeholder="Enter slide-in title"
                      value={toggleValues?.leadCaptureTitle || ''}
                      onChange={(e) => handleInputChange('leadCaptureTitle', e.target.value)}
                      onFocus={() => handleFocus('leadCaptureTitle')}
                      onBlur={handleBlur}
                      maxLength={50}
                      disabled={isRestricted}
                      data-testid="LeadCapture-email-input-title"
                    />
                    {focusedField === 'leadCaptureTitle' && (
                      <span className={styles.charCounter}>{toggleValues?.leadCaptureTitle?.length || 0}/50</span>
                    )}
                    {errors?.toggleValues?.leadCaptureTitle && (
                      <div className={styles.errorText}>{errors?.toggleValues?.leadCaptureTitle}</div>
                    )}
                  </div>

                  <div className={styles.inputWithCounter}>
                    <textarea
                      className={styles.previewInput}
                      placeholder="Enter a description explaining why visitors should provide their email"
                      value={toggleValues?.leadCaptureDescription || ''}
                      onChange={(e) => handleInputChange('leadCaptureDescription', e.target.value)}
                      onFocus={() => handleFocus('leadCaptureDescription')}
                      onBlur={handleBlur}
                      maxLength={120}
                      disabled={isRestricted}
                      style={{ minHeight: '60px', resize: 'vertical' }}
                      data-testid="LeadCapture-email-input-description"
                    />
                    {focusedField === 'leadCaptureDescription' && (
                      <span className={styles.charCounter}>
                        {toggleValues?.leadCaptureDescription?.length || 0}/120
                      </span>
                    )}
                    {errors?.toggleValues?.leadCaptureDescription && (
                      <div className={styles.errorText}>{errors?.toggleValues?.leadCaptureDescription}</div>
                    )}
                  </div>

                  <div className={styles.emailInputPreview}>
                    <span className={styles.emailInputPreviewText}>
                      The user&apos;s email input field will be shown here
                    </span>
                  </div>

                  <div
                    className={`${styles.ctaContainer} ${showLinkTooltip && focusedField === 'leadCaptureCtaTextEmail' ? styles.ctaContainerFocused : ''}`}
                  >
                    <div className={styles.ctaButtonWrapper}>
                      <input
                        type="text"
                        className={styles.ctaButtonInput}
                        placeholder="Enter button text"
                        value={toggleValues?.leadCaptureCtaText || 'Continue'}
                        onChange={(e) => handleInputChange('leadCaptureCtaText', e.target.value)}
                        onFocus={() => handleFocus('leadCaptureCtaTextEmail')}
                        onBlur={() => handleBlur('leadCaptureCtaTextEmail')}
                        maxLength={25}
                        disabled={isRestricted}
                      />
                      {focusedField === 'leadCaptureCtaTextEmail' && (
                        <span className={styles.charCounter}>{toggleValues?.leadCaptureCtaText?.length || 0}/25</span>
                      )}
                      {errors?.toggleValues?.leadCaptureCtaText && (
                        <div className={styles.errorText}>{errors?.toggleValues?.leadCaptureCtaText}</div>
                      )}
                    </div>

                    {showLinkTooltip && focusedField === 'leadCaptureCtaTextEmail' && (
                      <div
                        className={`${styles.linkInputContainer} ${linkInputFocused ? styles.linkInputContainerFocused : ''}`}
                        onClick={() => {
                          const linkInput = document.querySelector(`.${styles.linkInput}`)
                          if (linkInput) linkInput.focus()
                        }}
                      >
                        <div className={styles.linkInputWrapper}>
                          <img
                            src="/assets/icons/link.svg"
                            alt="link"
                            width="16"
                            height="16"
                            className={styles.grayIcon}
                          />
                          <input
                            type="text"
                            className={styles.linkInput}
                            placeholder="Type or paste redirect URL (optional)"
                            value={toggleValues?.leadCaptureCtaLink || ''}
                            onChange={(e) => handleInputChange('leadCaptureCtaLink', e.target.value)}
                            onFocus={handleLinkInputFocus}
                            onBlur={handleLinkInputBlur}
                            disabled={isRestricted}
                          />
                        </div>
                        {errors?.toggleValues?.leadCaptureCtaLink && (
                          <div className={styles.errorText}>{errors?.toggleValues?.leadCaptureCtaLink}</div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export default LeadCaptureSection
